import type { TemplateConfig } from "./configType";

const seoTitle =
  "AGBAZA — Plateforme nationale des dossiers médicaux au Bénin";
const seoDescription =
  "Centralisation des dossiers médicaux des populations du Bénin : suivi homogène de la santé, historique accessible aux professionnels, recherche et statistiques pour les autorités sanitaires, alertes et prévention. Fiabilisation et sécurisation des données de santé.";

const templateConfig: TemplateConfig = {
  name: "AGBAZA",
  seo: {
    title: seoTitle,
    description: seoDescription,
  },
  backgroundGrid: false,
  logo: "/favicon.svg",
  ogImage: "/og-image.png",
  theme: "previn",
  forceTheme: false,
  showThemeSwitch: true,
  /** Liens stores : surchargés par VITE_GOOGLE_PLAY_URL / VITE_APP_STORE_URL dans `resolvedTemplateConfig`. */
  googlePlayLink: "https://play.google.com/store",
  appStoreLink: "https://apps.apple.com/",
  footer: {
    legalLinks: {
      termsAndConditions: false,
      cookiesPolicy: false,
      privacyPolicy: false,
    },
    links: [
      { href: "#features", title: "Fonctionnalités" },
      { href: "#how-it-works", title: "Parcours" },
      { href: "#acteurs", title: "Acteurs" },
      { href: "#faq", title: "FAQ" },
    ],
  },
  topNavbar: {
    cta: "Découvrir la plateforme",
    disableWidthAnimation: false,
    links: [
      { href: "#features", title: "Fonctionnalités" },
      { href: "#how-it-works", title: "Parcours" },
      { href: "#acteurs", title: "Acteurs" },
      { href: "#faq", title: "FAQ" },
    ],
  },
  appBanner: {
    id: "app-banner",
    title: "AGBAZA sur mobile et web",
    subtitle:
      "Consultez ou enrichissez les dossiers médicaux, accédez aux ordonnances et aux indicateurs selon vos droits. Une même plateforme pour les professionnels, les patients et le pilotage institutionnel au Bénin.",
    screenshots: [
      "/mobile-phone-img/1.webp",
      "/mobile-phone-img/2.webp",
      "/mobile-phone-img/3.webp",
    ],
  },
  home: {
    seo: {
      title: seoTitle,
      description: seoDescription,
    },
    partners: {
      title: "Identité, soins et pilotage sanitaire",
      logos: [
        "/misc/companies/apple.svg",
        "/misc/companies/aws.svg",
        "/misc/companies/google.svg",
        "/misc/companies/tumblr.svg",
      ],
    },
    testimonials: {
      id: "testimonials",
      title: "Ce que les acteurs en attendent",
      subtitle:
        "Un dossier unique, des droits maîtrisés et des indicateurs pour décider",
      cards: [
        {
          name: "Médecin hospitalier",
          comment:
            "Voir l’historique d’un patient sur un seul dossier évite les oublis et les incohérences entre structures. Les consultations et prescriptions restent tracées.",
        },
        {
          name: "Infirmier coordinateur",
          comment:
            "Les vaccinations, les allergies et les traitements en cours sont au même endroit : on gagne du temps et on réduit les risques à l’admission.",
        },
        {
          name: "Patient",
          comment:
            "Je retrouve mon dossier et mes ordonnances avec mon identifiant. Je comprends ce que je peux consulter et ce qui est réservé aux soignants.",
        },
        {
          name: "Agence régionale de santé",
          comment:
            "Filtrer par zone, âge ou pathologie et sortir des agrégats nous aide à prioriser les actions sans manipuler des fichiers disparates.",
        },
        {
          name: "Pilotage ministériel",
          comment:
            "Indicateurs de santé, de performance et de suivi des politiques publiques sur un tableau de bord : c’est l’ambition du cahier des charges pour le système.",
        },
      ],
    },
    howItWorks: {
      id: "how-it-works",
      title: "Comment la plateforme s’articule",
      subtitle:
        "De l’identification fiable des personnes au pilotage sanitaire : les grandes étapes prévues par le cahier des charges AGBAZA.",
      steps: [
        {
          title: "Identifiant national unique (NIP)",
          subtitle:
            "Chaque citoyen est reconnu via un identifiant obligatoire (ex. NIP délivré par l’ANIP), pour éviter les doublons de comptes et les dossiers fragmentés.",
          image: "/plateforme-articule/identifiant-nip.webp",
        },
        {
          title: "Authentification des professionnels",
          subtitle:
            "Les professionnels de santé sont authentifiés et autorisés — par exemple via le numéro d’ordre (CNOM pour les médecins) — pour sécuriser la saisie et les modifications cliniques.",
          image: "/plateforme-articule/authentification-professionel.webp",
        },
        {
          title: "Dossier médical numérique",
          subtitle:
            "Création et consultation des dossiers : identité, antécédents, consultations, analyses, vaccinations, etc. Les patients accèdent à leur espace avec identifiant et mot de passe.",
          image: "/plateforme-articule/dossier-medical-numerique.webp",
        },
        {
          title: "Recherche & statistiques",
          subtitle:
            "Filtres sur les dossiers (localisation, âge, sexe, pathologie…) et production d’indicateurs, rapports, tableaux de bord et visualisations pour les autorités sanitaires.",
          image: "/plateforme-articule/recherche-statistique.webp",
        },
        {
          title: "Alerte & prévention",
          subtitle:
            "Détection et signalement de situations suspectes ou d’urgences, notifications aux acteurs concernés, diffusion de messages de sensibilisation adaptés aux profils.",
          image: "/plateforme-articule/alerte-prevision.webp",
        },
      ],
    },
    features: {
      id: "features",
      title: "Les fonctionnalités prévues pour AGBAZA",
      subtitle:
        "Centraliser, analyser et prévenir : les piliers décrits dans le cahier des charges pour le ministère de la Santé du Bénin.",
      cards: [
        {
          title: "Dossiers médicaux centralisés",
          subtitle:
            "Création et consultation par les professionnels (médecins, infirmiers, pharmaciens…) : identité, antécédents, consultations, prescriptions, analyses, vaccinations. Accès patient sécurisé pour consulter et compléter selon les règles définies.",
          icon: "/fonctionnalite/dossier-medicaux.webp",
        },
        {
          title: "Recherche & statistiques",
          subtitle:
            "Recherche et filtrage des dossiers pour les professionnels et les autorités sanitaires ; statistiques, rapports, tableaux de bord, cartes et graphiques pour la situation sanitaire du pays.",
          icon: "/fonctionnalite/recherche-statistique.webp",
        },
        {
          title: "Alerte & prévention",
          subtitle:
            "Signalement de cas suspects, épidémies ou urgences ; alertes par SMS, e-mail ou notification ; messages de sensibilisation ciblés selon l’âge, le sexe ou la pathologie.",
          icon: "/fonctionnalite/alerte-prevention.webp",
        },
        {
          title: "Fiabilité des données",
          subtitle:
            "NIP vérifié auprès de l’ANIP pour éviter les doubles comptes ; authentification des soignants (ex. numéro d’ordre CNOM) ; moindre privilège pour les patients sur le dossier et les ordonnances.",
          icon: "/fonctionnalite/fiabilite-donnees.webp",
        },
      ],
    },
    faq: {
      id: "faq",
      title: "Questions fréquentes",
      qa: [
        {
          question: "Quel est l’objectif d’AGBAZA ?",
          answer:
            "Centraliser les dossiers médicaux des populations du Bénin pour un suivi homogène, permettre à tout professionnel de santé de connaître l’historique d’un patient, et fiabiliser des données aujourd’hui souvent dispersées ou incomplètes. Le projet vise à être remis au ministère de la Santé, propriétaire et gestionnaire.",
        },
        {
          question: "Comment éviter deux dossiers pour la même personne ?",
          answer:
            "Le cahier des charges prévoit un identifiant unique et obligatoire (notamment le NIP délivré par l’ANIP), avec vérification de validité et contrôle qu’aucun compte n’existe déjà pour ce NIP avant création d’un dossier.",
        },
        {
          question: "Qui peut modifier les données médicales ?",
          answer:
            "Les éditions sensibles sont réservées aux professionnels de santé authentifiés et autorisés — par exemple vérification du numéro d’ordre des médecins via le CNOM-BENIN, avec des droits selon spécialité, statut et établissement.",
        },
        {
          question: "Que peuvent faire les patients dans leur espace ?",
          answer:
            "Le document prévoit un contrôle d’accès au moindre privilège : le patient s’authentifie avec son identifiant unique et son mot de passe, avec des droits de consultation ciblés sur son dossier et ses ordonnances, afin d’éviter les modifications non autorisées des données cliniques.",
        },
        {
          question: "Que contient le tableau de bord ministériel ?",
          answer:
            "Des indicateurs de santé publique (mortalité, morbidité, prévalence, groupes spécifiques…), de performance du système de santé (couverture, qualité, satisfaction, efficience) et de suivi des politiques et situations particulières (plans nationaux, alertes, épidémies).",
        },
        {
          question: "AGBAZA remplace-t-il le jugement clinique ?",
          answer:
            "Non : la plateforme est un outil de dossier partagé, d’analyse et d’alerte. Les décisions de soins restent la responsabilité des professionnels et des institutions selon le cadre légal et déontologique.",
        },
      ],
    },
    header: {
      headline:
        "Une plateforme nationale pour un suivi de santé homogène au Bénin",
      subtitle:
        "AGBAZA regroupe les dossiers médicaux des populations pour que chaque professionnel de santé dispose d’un historique fiable, et que le ministère de la Santé puisse piloter par des indicateurs et des statistiques nationales.",
      screenshots: [
        "/mobile-phone-img/1.webp",
        "/mobile-phone-img/2.webp",
        "/mobile-phone-img/3.webp",
      ],
      usersDescription:
        "Soignants, patients et autorités sanitaires sur une même base",
      headlineMark: [5, 9],
    },
    pricing: {
      id: "acteurs",
      title: "Les acteurs de la plateforme",
      subtitle:
        "Professionnels de santé, patients et autorités : des responsabilités distinctes pour sécuriser les données et servir le système de santé.",
      actionText: "Accéder aux espaces",
      plans: [
        {
          title: "Professionnels de santé",
          price: "Création & suivi des dossiers",
          href: "/professionnel/connexion",
          rows: [
            "Médecins, infirmiers, pharmaciens, etc. : saisie et consultation des informations de santé",
            "Authentification et habilitations (ex. numéro d’ordre pour les médecins)",
            "Consultations, prescriptions, analyses, vaccinations tracées sur le dossier",
          ],
        },
        {
          title: "Patients & citoyens",
          price: "Accès à son dossier",
          featured: true,
          href: "/patient/connexion",
          rows: [
            "Connexion avec identifiant unique et mot de passe",
            "Consultation et compléments selon les règles de la plateforme",
            "Droits de lecture ciblés sur le dossier et les ordonnances (moindre privilège)",
          ],
        },
        {
          title: "Centres de santé et autorités sanitaires",
          price: "Recherche & pilotage",
          href: "/etablissement/connexion",
          rows: [
            "Ministère, agences régionales : recherche, filtres et statistiques sur les populations",
            "Rapports, tableaux de bord, cartes et graphiques",
            "Tableau de bord centralisé : indicateurs de santé, de performance et de suivi des politiques",
          ],
        },
      ],
    },
  },
  privacyPolicy: {
    seo: {
      title: "Politique de confidentialité — AGBAZA",
      description:
        "Comment AGBAZA traite les données personnelles dans le cadre du dispositif de santé publique.",
    },
    content: `# Politique de confidentialité

**Dernière mise à jour :** à compléter par l’éditeur

## Introduction

L’application **AGBAZA** (ci-après « l’Application ») est développée et maintenue par **URMAPha** au titre d’une plateforme de centralisation des dossiers médicaux et de pilotage sanitaire au Bénin. La présente politique décrit comment des données personnelles peuvent être traitées lors de l’utilisation de l’Application et du site d’information associé.

## Données susceptibles d’être collectées

- **Compte et authentification :** identifiants, informations nécessaires à la connexion et à l’usage du mécanisme OTP (code à usage unique).
- **Données de santé :** informations du dossier médical (consultations, prescriptions, analyses, vaccinations, etc.), pièces jointes et médias transmis dans le cadre des soins ou du pilotage autorisé.
- **Données de contexte :** localisation, établissement de santé, profil (patient, professionnel, autorité), selon les référentiels et habilitations définis côté système.
- **Données techniques :** journaux techniques, type d’appareil, identifiants de session dans la limite nécessaire au bon fonctionnement et à la sécurité.

## Finalités du traitement

- Assurer la saisie structurée et la remontée des données vers le serveur central.
- Permettre la traçabilité des saisies (établissement, professionnel, type de formulaire) et le pilotage institutionnel.
- Garantir la sécurité d’accès (sessions, jetons, procédures d’identification adaptées aux environnements hospitaliers).

## Base légale et destinataires

Les traitements sont fondés sur l’exécution de missions de santé publique et l’intérêt légitime du dispositif, dans le respect du cadre applicable au Bénin. Les données peuvent être accessibles aux acteurs habilités (établissement, supervision, administration) conformément aux règles définies dans le système.

## Conservation

Les durées de conservation sont déterminées par les besoins du dispositif et par les obligations légales ou réglementaires applicables.

## Vos droits

Selon le cadre applicable, vous pouvez exercer des droits d’accès, de rectification, de limitation ou d’opposition dans les conditions prévues par la loi. Pour toute demande, contactez l’éditeur aux coordonnées indiquées par l’organisme responsable du déploiement.

## Contact

**URMAPha** — coordonnées à compléter par l’éditeur (adresse, courriel, téléphone).

---

*Ce texte est fourni à titre d’information et doit être validé juridiquement par l’autorité compétente et l’éditeur.*
`,
  },
  cookiesPolicy: {
    seo: {
      title: "Politique des cookies — AGBAZA",
      description:
        "Utilisation des cookies et technologies similaires sur le site AGBAZA.",
    },
    content: `# Politique des cookies

**Dernière mise à jour :** à compléter par l’éditeur

## Introduction

Le site présentant **AGBAZA** peut utiliser des cookies ou stockage local strictement nécessaires au fonctionnement technique (préférences d’affichage, session) ou, le cas échéant, à des mesures d’audience anonymisées.

## Types de cookies

- **Cookies nécessaires :** maintien de la session, sécurité, préférences essentielles.
- **Cookies de mesure d’audience :** uniquement s’ils sont mis en œuvre et documentés séparément.

## Vos choix

Vous pouvez configurer votre navigateur pour refuser ou supprimer les cookies ; certaines fonctionnalités du site pourraient alors être dégradées.

## Contact

**URMAPha** — coordonnées à compléter.

---

*Texte d’information à adapter selon les outils réellement déployés sur ce site.*
`,
  },
  termsAndConditions: {
    seo: {
      title: "Conditions d’utilisation — AGBAZA",
      description:
        "Conditions générales d’utilisation de l’application et du site AGBAZA.",
    },
    content: `# Conditions d’utilisation

**Dernière mise à jour :** à compléter par l’éditeur

## Objet

Les présentes conditions régissent l’accès à l’**Application AGBAZA** et, le cas échéant, au site d’information associé. L’Application est éditée par **URMAPha** dans le cadre d’un dispositif de santé publique au Bénin.

## Comptes et habilitations

L’accès aux fonctionnalités est subordonné à la création de comptes et à des rôles (patient, professionnel de santé, autorité sanitaire, etc.) attribués selon le cadre défini par le ministère de la Santé ou l’organisme responsable du déploiement. Vous vous engagez à fournir des informations exactes et à ne pas partager vos identifiants.

## Usage attendu

Vous vous engagez à utiliser l’Application conformément aux règles déontologiques et institutionnelles applicables aux professionnels de santé et aux structures habilitées.

## Propriété intellectuelle

Les éléments de l’Application (interface, marque AGBAZA, documentation) sont protégés. Toute reproduction non autorisée est interdite. La marque et le périmètre fonctionnel peuvent évoluer selon les conventions avec le ministère de la Santé du Bénin.

## Limitation de responsabilité

L’Application est fournie dans le cadre du dispositif décrit dans la documentation officielle. URMAPha ne saurait être tenue responsable d’un usage non conforme ou d’interruptions indépendantes de sa volonté.

## Droit applicable

Les présentes conditions sont soumises au droit en vigueur au **Bénin**. Les tribunaux compétents sont ceux du ressort défini par l’organisme responsable du déploiement, sauf disposition impérative contraire.

## Contact

**URMAPha** — coordonnées à compléter.

---

*Document d’information à valider juridiquement ; ne remplace pas les conventions spécifiques entre les parties du dispositif.*
`,
  },
};

export default templateConfig;
